import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ShiftAddress, ShiftNotes, shiftParticipantNames } from '@/components/shared/ShiftAddressNotes';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { clockInWindow } from '@/lib/clockInWindow';

const startKey = (s) => `${s.date || ''}T${(s.start_time || '').padStart(5, '0')}`;

// Past shifts are hidden: a shift that has ended drops off unless it is still running (clocked in, not yet out).
const isPast = (s, nowMs) => clockInWindow(s, nowMs).state === 'ended' && s.status !== 'IN_PROGRESS';

// Per-staff action label for 2:1 multi-staff shifts: each worker drives
// their button off their own staff_attendance entry, not the shift-level
// status (which would mis-label the 2nd worker as "Check Out" before they
// ever checked in). Check In is only offered inside the clock-in window (2 h before start until the end).
function actionLabel(shift, userId, nowMs) {
  const assigned = (shift.assigned_staff_ids && shift.assigned_staff_ids.length)
    ? shift.assigned_staff_ids
    : (shift.assigned_staff_id ? [shift.assigned_staff_id] : []);
  const isMulti = assigned.length > 1;
  const mine = (shift.staff_attendance || {})[userId] || {};
  const myIn = isMulti ? mine.check_in_time : (mine.check_in_time || shift.check_in_time);
  const myOut = isMulti ? mine.check_out_time : (mine.check_out_time || shift.check_out_time);
  if (!myIn && (shift.status === 'ASSIGNED' || shift.status === 'IN_PROGRESS') && clockInWindow(shift, nowMs).state === 'open') return 'Check In';
  if (myIn && !myOut) return 'Check Out';
  return 'View';
}

export default function MyRoster({ insideStaffPortal = false } = {}) {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [nowMs, setNowMs] = useState(Date.now());
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Upcoming shifts only (server keeps from yesterday on, for overnight shifts), every page.
        let all = [];
        for (let page = 1; page <= 50; page += 1) {
          const res = await api.get('/roster/my-shifts', { params: { upcoming: true, page, pageSize: 100 } });
          // Tolerate both paginated `{items}` and legacy `{data}` shapes.
          const items = res.data.items || res.data.data || [];
          all = all.concat(items);
          if (items.length < 100 || all.length >= (res.data.total ?? all.length)) break;
        }
        setShifts(all);
      } catch (e) {
        toast.error('Failed to load roster');
      }
    };
    fetchData();
  }, []);
  useEffect(() => { const t = setInterval(() => setNowMs(Date.now()), 30000); return () => clearInterval(t); }, []);

  const rows = shifts.filter((s) => !isPast(s, nowMs)).sort((a, b) => startKey(a).localeCompare(startKey(b)));

  return (
    <div className="space-y-6 animate-fade-in">
      {!insideStaffPortal && (
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Roster</h1>
          <p className="text-muted-foreground mt-1">Your assigned shifts</p>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Participant / Location</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No upcoming shifts</TableCell></TableRow>
              ) : rows.map((s) => (
                <TableRow key={s.id} className="hover:bg-muted/60">
                  <TableCell className="font-mono text-xs tabular-nums">{s.date}</TableCell>
                  <TableCell className="font-mono text-xs tabular-nums">{s.start_time}-{s.end_time}</TableCell>
                  {/* client#6: no Service / Status for staff · client#8: notes + where, without opening the shift */}
                  <TableCell className="max-w-[260px]">
                    <p className="font-medium">{shiftParticipantNames(s) || s.house_name || 'N/A'}</p>
                    <ShiftAddress shift={s} compact />
                  </TableCell>
                  <TableCell className="max-w-[320px]" data-testid={`my-roster-notes-${s.id}`}>
                    <ShiftNotes notes={s.notes} compact />
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/staff/shift/${s.id}`)} data-testid={`shift-action-${s.id}`}>
                      {actionLabel(s, user?.id, nowMs)}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
